import React, {useEffect, useMemo, useRef, useState} from 'react';
import {TableBodyProps} from '@src/@types/components/List';
import {IEmptyModel} from '@src/@interfaces/IEmptyModel';
import {IOrderProductModel} from '@src/@interfaces/models/Order/IOrderProductModel';
import OrderProductUpdateForm from '@views/order-products/OrderProductUpdateForm';
import config from '@src/helpers/config';
import classnames from '@src/helpers/classnames';
import {useLocation, useNavigate} from 'react-router-dom';

type RenderRowParams<Model extends IEmptyModel> = Parameters<Required<TableBodyProps<Model>>['renderRow']>;

type Props<Model extends IEmptyModel> = {
    properties: RenderRowParams<Model>[0],
    baseRenderer: RenderRowParams<Model>[1],
};

let lastSearchString: string | null = null;
let lastResult: URLSearchParams | null = null;
const makeURLSearchParams = (search: string): URLSearchParams => {
    if (search === lastSearchString && lastResult) {
        return lastResult;
    }

    lastSearchString = search;

    return lastResult = new URLSearchParams(search);
};

const OrderProductUpdateFormMemoized = React.memo(OrderProductUpdateForm);

export default function OrderProductsListRow<Model extends IEmptyModel = IOrderProductModel>(props: Props<Model>) {
    const GET_PARAM_NAME = 'productId';

    const haveToSaveRam = config('client.try-to-save-ram', false);
    const orderProductId = props.properties.model?.id;

    const {search, hash} = useLocation();
    const navigate = useNavigate();
    const locationRef = useRef({search, hash});
    const navigateTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

    const [isDisplayed, setIsDisplayed] = useState(
            makeURLSearchParams(search).get(GET_PARAM_NAME) === String(orderProductId)
    );
    const [isLoaded, setIsLoaded] = useState(isDisplayed);

    // clear navigate timeout
    useEffect(() => () => clearTimeout(navigateTimeoutRef.current), []);

    // update locationRef when needed
    useEffect(() => {
        locationRef.current = {search, hash};
    }, [hash, search]);

    // sync state to url params
    useEffect(() => {
        const isShouldBeDisplayed = makeURLSearchParams(search).get(GET_PARAM_NAME) === String(orderProductId);
        if (isShouldBeDisplayed !== isDisplayed) {
            setIsLoaded(true);
            setIsDisplayed(isShouldBeDisplayed);
        }
    }, [isDisplayed, orderProductId, search]);

    const {rowRendererExtraProps, formProps} = useMemo(() => {
        const toggleIsDisplayedState = (newState: boolean | null = null) => {
            setIsDisplayed(was => {
                const newVal = newState ?? !was;
                const params = new URLSearchParams(locationRef.current.search);

                if (newVal) {
                    setIsLoaded(true);
                    params.set(GET_PARAM_NAME, String(orderProductId));
                } else {
                    params.delete(GET_PARAM_NAME);
                }

                clearTimeout(navigateTimeoutRef.current);
                navigateTimeoutRef.current = setTimeout(() => {
                    navigate({
                        search: params.toString(),
                        hash: locationRef.current.hash,
                    });
                }, 10);

                return newVal;
            });
        };

        const rowRendererExtraProps = {
            onClick: () => void toggleIsDisplayedState(),
        };

        const formProps = {
            onCancel: () => void toggleIsDisplayedState(false),
            urlParams: {id: orderProductId ?? 'create'},
        };

        return {toggleIsDisplayedState, rowRendererExtraProps, formProps};
    }, [navigate, orderProductId]);

    if (!isDisplayed && haveToSaveRam) {
        return <>{props.baseRenderer(rowRendererExtraProps)}</>;
    }

    const {onCancel, urlParams} = formProps;

    return <>
        {props.baseRenderer(rowRendererExtraProps)}

        <tr className={classnames('models-list__content-table__extra-row', {
            'd-none': !isDisplayed,
        })}>
            <td colSpan={props.properties.context?.columnsNumber ?? 1}>
                {isLoaded ? <OrderProductUpdateFormMemoized
                        controllerName="OrderProducts"
                        forceUrlParams={urlParams}
                        onCancel={onCancel}
                        isIsolatedComponent
                /> : null}
            </td>
        </tr>
    </>;
}
